/**
 * 可视化性能优化工具
 */

/**
 * 判断是否应该使用 Canvas 而非 SVG
 * @param {number} dataPoints - 数据点数量
 * @param {number} threshold - 阈值（默认 1000）
 * @returns {boolean}
 */
export function shouldUseCanvas(dataPoints, threshold = 1000) {
  return dataPoints > threshold
}

/**
 * 使用 requestAnimationFrame 优化动画
 * @param {Function} callback - 动画回调函数
 * @returns {Object} 控制对象 {start, stop, isRunning}
 */
export function createAnimationLoop(callback) {
  let animationId = null
  let isRunning = false
  let lastTime = 0
  
  function animate(currentTime) {
    if (!isRunning) return
    
    const deltaTime = currentTime - lastTime
    lastTime = currentTime
    
    callback(deltaTime, currentTime)
    
    animationId = requestAnimationFrame(animate)
  }
  
  return {
    start() {
      if (isRunning) return
      isRunning = true
      lastTime = performance.now()
      animationId = requestAnimationFrame(animate)
    },
    
    stop() {
      isRunning = false
      if (animationId !== null) {
        cancelAnimationFrame(animationId)
        animationId = null
      }
    },
    
    get isRunning() {
      return isRunning
    }
  }
}

/**
 * 节流渲染函数（使用 requestAnimationFrame）
 * @param {Function} renderFn - 渲染函数
 * @returns {Function} 节流后的渲染函数
 */
export function throttleRender(renderFn) {
  let rafId = null
  let pendingArgs = null
  
  return function (...args) {
    pendingArgs = args
    
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        renderFn.apply(this, pendingArgs)
        rafId = null
        pendingArgs = null
      })
    }
  }
}

/**
 * 防抖渲染函数
 * @param {Function} renderFn - 渲染函数
 * @param {number} delay - 延迟时间（毫秒）
 * @returns {Function} 防抖后的渲染函数
 */
export function debounceRender(renderFn, delay = 300) {
  let timeoutId = null
  let rafId = null
  
  return function (...args) {
    // Clear existing timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
    
    // Clear existing RAF
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }
    
    // Set new timeout
    timeoutId = setTimeout(() => {
      rafId = requestAnimationFrame(() => {
        renderFn.apply(this, args)
        timeoutId = null
        rafId = null
      })
    }, delay)
  }
}

/**
 * 数据抽样 - 减少渲染点数
 * @param {Array} data - 原始数据数组
 * @param {number} maxPoints - 最大点数
 * @param {string} method - 抽样方法 ('uniform', 'lttb')
 * @returns {Array} 抽样后的数据
 */
export function downsampleData(data, maxPoints, method = 'uniform') {
  if (!data || data.length <= maxPoints) {
    return data
  }
  
  if (method === 'lttb') {
    return downsampleLTTB(data, maxPoints)
  }
  
  // Uniform sampling (default)
  return downsampleUniform(data, maxPoints)
}

/**
 * 均匀抽样
 * @param {Array} data - 原始数据
 * @param {number} maxPoints - 最大点数
 * @returns {Array}
 */
function downsampleUniform(data, maxPoints) {
  const step = Math.ceil(data.length / maxPoints)
  const result = []
  
  for (let i = 0; i < data.length; i += step) {
    result.push(data[i])
  }
  
  // Always include last point
  if (result[result.length - 1] !== data[data.length - 1]) {
    result.push(data[data.length - 1])
  }
  
  return result
}

/**
 * Largest Triangle Three Buckets (LTTB) 抽样算法
 * 保留数据的视觉特征
 * @param {Array} data - 原始数据 [{x, y}, ...]
 * @param {number} threshold - 目标点数
 * @returns {Array}
 */
function downsampleLTTB(data, threshold) {
  if (threshold >= data.length || threshold === 0) {
    return data
  }
  
  const sampled = []
  const bucketSize = (data.length - 2) / (threshold - 2)
  
  // Always add first point
  sampled.push(data[0])
  
  let a = 0
  
  for (let i = 0; i < threshold - 2; i++) {
    // Calculate point average for next bucket
    let avgX = 0
    let avgY = 0
    const avgRangeStart = Math.floor((i + 1) * bucketSize) + 1
    const avgRangeEnd = Math.floor((i + 2) * bucketSize) + 1
    const avgRangeLength = avgRangeEnd - avgRangeStart
    
    for (let j = avgRangeStart; j < avgRangeEnd; j++) {
      const point = data[j]
      avgX += point.x || j
      avgY += point.y || point
    }
    avgX /= avgRangeLength
    avgY /= avgRangeLength
    
    // Get the range for this bucket
    const rangeOffs = Math.floor(i * bucketSize) + 1
    const rangeTo = Math.floor((i + 1) * bucketSize) + 1
    
    // Point a
    const pointA = data[a]
    const pointAX = pointA.x || a
    const pointAY = pointA.y || pointA
    
    let maxArea = -1
    let maxAreaPoint = null
    
    for (let j = rangeOffs; j < rangeTo; j++) {
      const point = data[j]
      const pointX = point.x || j
      const pointY = point.y || point
      
      // Calculate triangle area
      const area = Math.abs(
        (pointAX - avgX) * (pointY - pointAY) -
        (pointAX - pointX) * (avgY - pointAY)
      ) * 0.5
      
      if (area > maxArea) {
        maxArea = area
        maxAreaPoint = point
        a = j
      }
    }
    
    sampled.push(maxAreaPoint)
  }
  
  // Always add last point
  sampled.push(data[data.length - 1])
  
  return sampled
}

/**
 * 批量渲染优化 - 将大量渲染操作分批执行
 * @param {Array} items - 要渲染的项目数组
 * @param {Function} renderFn - 渲染单个项目的函数
 * @param {number} batchSize - 每批处理的数量
 * @returns {Promise}
 */
export function batchRender(items, renderFn, batchSize = 100) {
  return new Promise((resolve) => {
    let index = 0
    
    function processBatch() {
      const end = Math.min(index + batchSize, items.length)
      
      for (let i = index; i < end; i++) {
        renderFn(items[i], i)
      }
      
      index = end
      
      if (index < items.length) {
        requestAnimationFrame(processBatch)
      } else {
        resolve()
      }
    }
    
    processBatch()
  })
}

/**
 * Canvas 性能优化 - 离屏渲染
 * @param {number} width - 画布宽度
 * @param {number} height - 画布高度
 * @param {Function} drawFn - 绘制函数
 * @returns {HTMLCanvasElement}
 */
export function createOffscreenCanvas(width, height, drawFn) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  
  const ctx = canvas.getContext('2d')
  drawFn(ctx)
  
  return canvas
}

/**
 * 检测设备性能等级
 * @returns {string} 'high', 'medium', 'low'
 */
export function detectPerformanceLevel() {
  // Check hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 2
  
  // Check device memory (if available)
  const memory = navigator.deviceMemory || 4
  
  // Check if mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
  
  if (isMobile) {
    return memory >= 4 ? 'medium' : 'low'
  }
  
  if (cores >= 8 && memory >= 8) {
    return 'high'
  } else if (cores >= 4 && memory >= 4) {
    return 'medium'
  } else {
    return 'low'
  }
}

/**
 * 根据性能等级调整渲染参数
 * @param {string} level - 性能等级
 * @returns {Object} 渲染参数
 */
export function getPerformanceConfig(level = null) {
  const detectedLevel = level || detectPerformanceLevel()
  
  const configs = {
    high: {
      maxDataPoints: 10000,
      animationFPS: 60,
      useAntialiasing: true,
      shadowsEnabled: true,
      particleCount: 1000
    },
    medium: {
      maxDataPoints: 5000,
      animationFPS: 30,
      useAntialiasing: true,
      shadowsEnabled: false,
      particleCount: 500
    },
    low: {
      maxDataPoints: 2000,
      animationFPS: 24,
      useAntialiasing: false,
      shadowsEnabled: false,
      particleCount: 200
    }
  }
  
  return configs[detectedLevel] || configs.medium
}

/**
 * FPS 监控器
 */
export class FPSMonitor {
  constructor() {
    this.frames = []
    this.lastTime = performance.now()
  }
  
  update() {
    const now = performance.now()
    const delta = now - this.lastTime
    this.lastTime = now
    
    this.frames.push(delta)
    
    // Keep only last 60 frames
    if (this.frames.length > 60) {
      this.frames.shift()
    }
  }
  
  getFPS() {
    if (this.frames.length === 0) return 0
    
    const avgDelta = this.frames.reduce((a, b) => a + b, 0) / this.frames.length
    return Math.round(1000 / avgDelta)
  }
  
  reset() {
    this.frames = []
    this.lastTime = performance.now()
  }
}

/**
 * 响应式 Canvas 尺寸配置选项
 * @typedef {Object} ResponsiveCanvasOptions
 * @property {number} [maxWidth=800] - 最大宽度（像素）
 * @property {number} [minWidth=280] - 最小宽度（像素）
 * @property {number|null} [aspectRatio=null] - 宽高比（如 50/31），null 表示使用默认 50:31
 * @property {number|null} [fixedHeight=null] - 固定高度（像素），如果提供则忽略 aspectRatio
 * @property {number} [padding=32] - 容器内边距（像素），用于计算可用宽度
 * @property {number} [devicePixelRatio=1.1] - 设备像素比上限（已废弃，由 setCanvasSize 处理）
 */

/**
 * 默认宽高比：50:31
 */
export const DEFAULT_ASPECT_RATIO = 50 / 31

/**
 * 计算响应式 Canvas 尺寸
 * @param {HTMLElement|null} container - 容器元素
 * @param {ResponsiveCanvasOptions} options - 配置选项
 * @returns {{width: number, height: number}} Canvas 尺寸（逻辑像素）
 */
export function calculateResponsiveCanvasSize(container, options = {}) {
  const {
    maxWidth = 800,
    minWidth = 280,
    aspectRatio = null,
    fixedHeight = null,
    padding = 32
  } = options

  // 确定使用的宽高比：优先 fixedHeight，其次 aspectRatio，最后默认 50:31
  const usedAspectRatio = fixedHeight !== null ? null : (aspectRatio !== null ? aspectRatio : DEFAULT_ASPECT_RATIO)

  // 如果没有容器，返回默认值
  if (!container) {
    const defaultWidth = Math.min(maxWidth, 600)
    const defaultHeight = fixedHeight || (usedAspectRatio ? Math.floor(defaultWidth / usedAspectRatio) : 420)
    return { width: defaultWidth, height: defaultHeight }
  }

  // 获取容器宽度
  const containerWidth = container.getBoundingClientRect().width || 600
  const availableWidth = Math.max(minWidth, containerWidth - padding)
  // 如果 maxWidth 是 Infinity，则完全利用容器宽度；否则限制在 maxWidth 内
  const width = maxWidth === Infinity ? availableWidth : Math.min(maxWidth, availableWidth)

  // 计算高度
  let height
  if (fixedHeight !== null) {
    height = fixedHeight
  } else if (usedAspectRatio) {
    height = Math.floor(width / usedAspectRatio)
  } else {
    height = 420 // 默认高度（向后兼容）
  }

  return { width, height }
}

/**
 * 设置 Canvas 的响应式尺寸（支持高DPI和逻辑坐标系）
 * 
 * CRITICAL: 保持绘图中文字宽高比，不因窗口缩放而拉伸任何文字
 * 
 * @param {HTMLCanvasElement} canvas - Canvas 元素
 * @param {number} width - 宽度（逻辑像素，用于绘制）
 * @param {number} height - 高度（逻辑像素）
 * @param {Object} [options={}] - 配置选项
 * @param {number} [options.devicePixelRatio] - 设备像素比上限，不设置则使用实际 devicePixelRatio
 * @param {boolean} [options.enableHighDPI=true] - 是否启用高DPI支持
 * @param {boolean} [options.adaptiveWidth=true] - 是否让CSS宽度自适应容器（100%）
 * @param {HTMLElement} [options.container] - 容器元素（当adaptiveWidth=true时，用于获取实际宽度）
 * @returns {{logicWidth: number, logicHeight: number, physicalWidth: number, physicalHeight: number, dpr: number}} 尺寸信息
 */
export function setCanvasSize(canvas, width, height, options = {}) {
  if (!canvas) return null

  const {
    devicePixelRatio = 1.1,
    enableHighDPI = true,
    adaptiveWidth = true,
    container = null
  } = options

  // CRITICAL: 如果使用自适应宽度，必须确保逻辑宽度匹配容器的实际宽度
  // 否则CSS宽度100%会导致内容被拉伸，文字宽高比会改变
  let actualLogicWidth = width
  if (adaptiveWidth) {
    // 获取容器的实际宽度（优先使用传入的container，否则使用canvas的父容器）
    const containerElement = container || (canvas.parentElement)
    if (containerElement) {
      const containerRect = containerElement.getBoundingClientRect()
      actualLogicWidth = containerRect.width || width
    }
    // 如果无法获取容器宽度，使用传入的width（向后兼容）
  }

  // 获取设备像素比
  const actualDPR = window.devicePixelRatio || 1
  const dpr = enableHighDPI 
    ? (devicePixelRatio !== undefined ? Math.min(devicePixelRatio, actualDPR) : actualDPR)
    : 1

  // 计算物理像素尺寸（用于实际绘制）
  // CRITICAL: 使用actualLogicWidth确保逻辑宽度匹配CSS宽度，避免文字拉伸
  const physicalWidth = Math.round(actualLogicWidth * dpr)
  const physicalHeight = Math.round(height * dpr)

  // 设置 canvas 实际像素尺寸
  // 注意：设置 width/height 会自动重置 canvas 状态，包括变换矩阵
  canvas.width = physicalWidth
  canvas.height = physicalHeight

  // 设置 CSS 显示尺寸
  if (adaptiveWidth) {
    // 自适应宽度：CSS宽度100%，完全填充容器
    // CRITICAL: actualLogicWidth已经匹配容器宽度，所以不会拉伸
    canvas.style.width = '100%'
    canvas.style.height = height + 'px'
    canvas.style.maxWidth = '100%'
  } else {
    // 固定宽度：使用指定的逻辑像素值
    canvas.style.width = width + 'px'
    canvas.style.height = height + 'px'
    canvas.style.maxWidth = '100%'
  }

  // 如果启用了高DPI，缩放上下文
  // 注意：必须在设置 width/height 之后调用，因为设置尺寸会重置变换矩阵
  if (enableHighDPI && dpr !== 1) {
    const ctx = canvas.getContext('2d')
    // 重置变换矩阵（虽然设置width/height已经重置了，但为了确保）
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(dpr, dpr)
  }

  // 保存尺寸信息到 canvas（供其他函数使用）
  // CRITICAL: 保存actualLogicWidth而不是传入的width，确保逻辑宽度匹配CSS宽度
  canvas._logicWidth = actualLogicWidth
  canvas._logicHeight = height
  canvas._physicalWidth = physicalWidth
  canvas._physicalHeight = physicalHeight
  canvas._dpr = dpr

  return {
    logicWidth: actualLogicWidth,  // 返回实际使用的逻辑宽度
    logicHeight: height,
    physicalWidth,
    physicalHeight,
    dpr
  }
}

